import { useCallback, useMemo, useState } from "react";

import type { BorgContextPin } from "../models/borg-types";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: switch on discriminated union with trivial cases
export const pinKey = (pin: BorgContextPin): string => {
	switch (pin.type) {
		case "component":
			return `component:${pin.name ?? ""}:${pin.urn ?? ""}:${pin.component_type ?? ""}`;
		case "topology_time":
			return `topology_time:${pin.point_ms ?? ""}:${pin.is_live ?? ""}`;
		case "telemetry_interval":
			return `telemetry_interval:${pin.start_ms ?? ""}:${pin.end_ms ?? ""}:${pin.range_label ?? ""}`;
		case "time_range":
			return `time_range:${pin.start_ms ?? ""}:${pin.end_ms ?? ""}:${pin.point_ms ?? ""}`;
		case "filter":
			return `filter:${pin.name ?? ""}:${pin.value ?? ""}`;
		case "query":
			return `query:${pin.query ?? ""}`;
	}
};

export interface ContextBarState {
	activePins: ReadonlyArray<BorgContextPin>;
	removePin: (pin: BorgContextPin) => void;
	resetRemoved: () => void;
	addPin: (pin: BorgContextPin) => void;
	removedPins: ReadonlyArray<BorgContextPin>;
}

export const useContextBarState = (collectedPins: ReadonlyArray<BorgContextPin>): ContextBarState => {
	const [removedKeys, setRemovedKeys] = useState<ReadonlySet<string>>(new Set());
	const [addedPins, setAddedPins] = useState<Array<BorgContextPin>>([]);

	const activePins = useMemo(
		() => [...collectedPins, ...addedPins].filter((pin) => !removedKeys.has(pinKey(pin))),
		[collectedPins, addedPins, removedKeys],
	);

	const removedPins = useMemo(
		() => collectedPins.filter((pin) => removedKeys.has(pinKey(pin))),
		[collectedPins, removedKeys],
	);

	const removePin = useCallback((pin: BorgContextPin) => {
		setRemovedKeys((prev) => new Set([...prev, pinKey(pin)]));
	}, []);

	const addPin = useCallback(
		(pin: BorgContextPin) => {
			const key = pinKey(pin);
			// If it was a previously removed auto-collected pin, just un-remove it
			setRemovedKeys((prev) => {
				if (!prev.has(key)) {
					return prev;
				}
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
			// Only add to addedPins if it's not already in collectedPins
			if (!collectedPins.some((p) => pinKey(p) === key)) {
				setAddedPins((prev) => {
					if (prev.some((p) => pinKey(p) === key)) {
						return prev;
					}
					return [...prev, pin];
				});
			}
		},
		[collectedPins],
	);

	const resetRemoved = useCallback(() => {
		setRemovedKeys(new Set());
		setAddedPins([]);
	}, []);

	return { activePins, removePin, resetRemoved, addPin, removedPins };
};
